import * as z from 'zod';

export const CreateFunctionRequest = /*#__PURE__*/ z.object({
  function: z
    .object({
      _id: z
        .string()
        .describe('Function ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the function is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the function.\n\nIgnored when creating a function.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the function was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the function was last updated.')
        .optional()
        .nullable(),
      functionExtensionId: z
        .string()
        .describe(
          'ID of the function type that defines when and how the function runs. This is the function type that the function is based on.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        ),
      functionName: z
        .string()
        .describe(
          "Display name for the function in the dashboard's function builder."
        )
        .max(100)
        .optional()
        .nullable(),
      activationStatus: z.enum(['ACTIVE', 'INACTIVE', 'DRAFT']).optional(),
      appId: z
        .string()
        .describe(
          'ID of the Wix app that defines the function type that the function is based on.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        ),
      functionExtensionName: z
        .string()
        .describe('Display name of the function type.')
        .min(1)
        .max(100)
        .optional()
        .nullable(),
      functionTemplateExtensionId: z
        .string()
        .describe('ID of the function template used to create this function.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      formTemplateExtensionId: z
        .string()
        .describe(
          'ID of the form template used to create this function. Included if the function was created using the [Builderless Productions](https://dev.wix.com/docs/api-reference/business-management/functions/builderless-productions/introduction) API.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      hasUnpublishedChanges: z
        .boolean()
        .describe(
          'Whether the function has changes that were made since it was last activated.'
        )
        .optional()
        .nullable(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, typically restricted from site members and visitors.'
            )
            .optional(),
          publicTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone with access to the entity, including site members and visitors.'
            )
            .optional(),
        })
        .describe(
          'Tags assigned to the function for organization and filtering.'
        )
        .optional(),
    })
    .describe('Function to create.'),
});
export const CreateFunctionResponse = /*#__PURE__*/ z.object({
  _id: z
    .string()
    .describe('Function ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the function is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the function.\n\nIgnored when creating a function.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the function was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the function was last updated.')
    .optional()
    .nullable(),
  functionExtensionId: z
    .string()
    .describe(
      'ID of the function type that defines when and how the function runs. This is the function type that the function is based on.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  functionName: z
    .string()
    .describe(
      "Display name for the function in the dashboard's function builder."
    )
    .max(100)
    .optional()
    .nullable(),
  activationStatus: z
    .enum(['ACTIVE', 'INACTIVE', 'DRAFT'])
    .describe('Activation status.')
    .optional(),
  appId: z
    .string()
    .describe(
      'ID of the Wix app that defines the function type that the function is based on.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  functionExtensionName: z
    .string()
    .describe('Display name of the function type.')
    .min(1)
    .max(100)
    .optional()
    .nullable(),
  functionTemplateExtensionId: z
    .string()
    .describe('ID of the function template used to create this function.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  formTemplateExtensionId: z
    .string()
    .describe(
      'ID of the form template used to create this function. Included if the function was created using the [Builderless Productions](https://dev.wix.com/docs/api-reference/business-management/functions/builderless-productions/introduction) API.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  hasUnpublishedChanges: z
    .boolean()
    .describe(
      'Whether the function has changes that were made since it was last activated.'
    )
    .optional()
    .nullable(),
  tags: z
    .object({
      privateTags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that require an additional permission in order to access them, typically restricted from site members and visitors.'
        )
        .optional(),
      publicTags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that are exposed to anyone with access to the entity, including site members and visitors.'
        )
        .optional(),
    })
    .describe('Tags assigned to the function for organization and filtering.')
    .optional(),
});
export const GetFunctionRequest = /*#__PURE__*/ z.object({
  functionId: z
    .string()
    .describe('ID of the function to retrieve.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetFunctionResponse = /*#__PURE__*/ z.object({
  _id: z
    .string()
    .describe('Function ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the function is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the function.\n\nIgnored when creating a function.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the function was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the function was last updated.')
    .optional()
    .nullable(),
  functionExtensionId: z
    .string()
    .describe(
      'ID of the function type that defines when and how the function runs. This is the function type that the function is based on.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  functionName: z
    .string()
    .describe(
      "Display name for the function in the dashboard's function builder."
    )
    .max(100)
    .optional()
    .nullable(),
  activationStatus: z
    .enum(['ACTIVE', 'INACTIVE', 'DRAFT'])
    .describe('Activation status.')
    .optional(),
  appId: z
    .string()
    .describe(
      'ID of the Wix app that defines the function type that the function is based on.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  functionExtensionName: z
    .string()
    .describe('Display name of the function type.')
    .min(1)
    .max(100)
    .optional()
    .nullable(),
  functionTemplateExtensionId: z
    .string()
    .describe('ID of the function template used to create this function.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  formTemplateExtensionId: z
    .string()
    .describe(
      'ID of the form template used to create this function. Included if the function was created using the [Builderless Productions](https://dev.wix.com/docs/api-reference/business-management/functions/builderless-productions/introduction) API.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  hasUnpublishedChanges: z
    .boolean()
    .describe(
      'Whether the function has changes that were made since it was last activated.'
    )
    .optional()
    .nullable(),
  tags: z
    .object({
      privateTags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that require an additional permission in order to access them, typically restricted from site members and visitors.'
        )
        .optional(),
      publicTags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that are exposed to anyone with access to the entity, including site members and visitors.'
        )
        .optional(),
    })
    .describe('Tags assigned to the function for organization and filtering.')
    .optional(),
});
export const UpdateFunctionRequest = /*#__PURE__*/ z.object({
  _id: z
    .string()
    .describe('Function ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  function: z
    .object({
      _id: z
        .string()
        .describe('Function ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the function is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the function.\n\nIgnored when creating a function.'
        ),
      _createdDate: z
        .date()
        .describe('Date and time the function was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the function was last updated.')
        .optional()
        .nullable(),
      functionExtensionId: z
        .string()
        .describe(
          'ID of the function type that defines when and how the function runs. This is the function type that the function is based on.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      functionName: z
        .string()
        .describe(
          "Display name for the function in the dashboard's function builder."
        )
        .max(100)
        .optional()
        .nullable(),
      activationStatus: z.enum(['ACTIVE', 'INACTIVE', 'DRAFT']).optional(),
      appId: z
        .string()
        .describe(
          'ID of the Wix app that defines the function type that the function is based on.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      functionExtensionName: z
        .string()
        .describe('Display name of the function type.')
        .min(1)
        .max(100)
        .optional()
        .nullable(),
      functionTemplateExtensionId: z
        .string()
        .describe('ID of the function template used to create this function.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      formTemplateExtensionId: z
        .string()
        .describe(
          'ID of the form template used to create this function. Included if the function was created using the [Builderless Productions](https://dev.wix.com/docs/api-reference/business-management/functions/builderless-productions/introduction) API.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      hasUnpublishedChanges: z
        .boolean()
        .describe(
          'Whether the function has changes that were made since it was last activated.'
        )
        .optional()
        .nullable(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, typically restricted from site members and visitors.'
            )
            .optional(),
          publicTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone with access to the entity, including site members and visitors.'
            )
            .optional(),
        })
        .describe(
          'Tags assigned to the function for organization and filtering.'
        )
        .optional(),
    })
    .describe('Function to update.'),
});
export const UpdateFunctionResponse = /*#__PURE__*/ z.object({
  _id: z
    .string()
    .describe('Function ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the function is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the function.\n\nIgnored when creating a function.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the function was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the function was last updated.')
    .optional()
    .nullable(),
  functionExtensionId: z
    .string()
    .describe(
      'ID of the function type that defines when and how the function runs. This is the function type that the function is based on.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  functionName: z
    .string()
    .describe(
      "Display name for the function in the dashboard's function builder."
    )
    .max(100)
    .optional()
    .nullable(),
  activationStatus: z
    .enum(['ACTIVE', 'INACTIVE', 'DRAFT'])
    .describe('Activation status.')
    .optional(),
  appId: z
    .string()
    .describe(
      'ID of the Wix app that defines the function type that the function is based on.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  functionExtensionName: z
    .string()
    .describe('Display name of the function type.')
    .min(1)
    .max(100)
    .optional()
    .nullable(),
  functionTemplateExtensionId: z
    .string()
    .describe('ID of the function template used to create this function.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  formTemplateExtensionId: z
    .string()
    .describe(
      'ID of the form template used to create this function. Included if the function was created using the [Builderless Productions](https://dev.wix.com/docs/api-reference/business-management/functions/builderless-productions/introduction) API.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  hasUnpublishedChanges: z
    .boolean()
    .describe(
      'Whether the function has changes that were made since it was last activated.'
    )
    .optional()
    .nullable(),
  tags: z
    .object({
      privateTags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that require an additional permission in order to access them, typically restricted from site members and visitors.'
        )
        .optional(),
      publicTags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that are exposed to anyone with access to the entity, including site members and visitors.'
        )
        .optional(),
    })
    .describe('Tags assigned to the function for organization and filtering.')
    .optional(),
});
export const DeleteFunctionRequest = /*#__PURE__*/ z.object({
  functionId: z
    .string()
    .describe('ID of the function to delete.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteFunctionResponse = /*#__PURE__*/ z.object({});
export const QueryFunctionsRequest = /*#__PURE__*/ z.object({
  query: z
    .intersection(
      z.object({
        filter: z
          .record(z.string(), z.any())
          .describe(
            'Filter object in the following format:\n`"filter" : {\n"fieldName1": "value1",\n"fieldName2":{"$operator":"value2"}\n}`\nExample of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`'
          )
          .optional()
          .nullable(),
        sort: z
          .array(
            z.object({
              fieldName: z
                .string()
                .describe('Name of the field to sort by.')
                .max(512)
                .optional(),
              order: z.enum(['ASC', 'DESC']).optional(),
            })
          )
          .max(5)
          .optional(),
      }),
      z.xor([
        z.object({ cursorPaging: z.never().optional() }),
        z.object({
          cursorPaging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Maximum number of items to return in the results.')
                .min(0)
                .max(100)
                .optional()
                .nullable(),
              cursor: z
                .string()
                .describe(
                  "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                )
                .max(16000)
                .optional()
                .nullable(),
            })
            .describe(
              'Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.'
            ),
        }),
      ])
    )
    .describe('Wix Query Language expression.'),
});
export const QueryFunctionsResponse = /*#__PURE__*/ z.object({
  functions: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Function ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the function is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the function.\n\nIgnored when creating a function.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the function was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the function was last updated.')
          .optional()
          .nullable(),
        functionExtensionId: z
          .string()
          .describe(
            'ID of the function type that defines when and how the function runs. This is the function type that the function is based on.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        functionName: z
          .string()
          .describe(
            "Display name for the function in the dashboard's function builder."
          )
          .max(100)
          .optional()
          .nullable(),
        activationStatus: z
          .enum(['ACTIVE', 'INACTIVE', 'DRAFT'])
          .describe('Activation status.')
          .optional(),
        appId: z
          .string()
          .describe(
            'ID of the Wix app that defines the function type that the function is based on.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        functionExtensionName: z
          .string()
          .describe('Display name of the function type.')
          .min(1)
          .max(100)
          .optional()
          .nullable(),
        functionTemplateExtensionId: z
          .string()
          .describe('ID of the function template used to create this function.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        formTemplateExtensionId: z
          .string()
          .describe(
            'ID of the form template used to create this function. Included if the function was created using the [Builderless Productions](https://dev.wix.com/docs/api-reference/business-management/functions/builderless-productions/introduction) API.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        hasUnpublishedChanges: z
          .boolean()
          .describe(
            'Whether the function has changes that were made since it was last activated.'
          )
          .optional()
          .nullable(),
        tags: z
          .object({
            privateTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that require an additional permission in order to access them, typically restricted from site members and visitors.'
              )
              .optional(),
            publicTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that are exposed to anyone with access to the entity, including site members and visitors.'
              )
              .optional(),
          })
          .describe(
            'Tags assigned to the function for organization and filtering.'
          )
          .optional(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Metadata for the paginated results.')
    .optional(),
});
export const BulkUpdateFunctionTagsRequest = /*#__PURE__*/ z.object({
  ids: z.array(z.string()).min(1).max(100),
  options: z.object({
    assignTags: z
      .object({
        privateTags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that require an additional permission in order to access them, typically restricted from site members and visitors.'
          )
          .optional(),
        publicTags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that are exposed to anyone with access to the entity, including site members and visitors.'
          )
          .optional(),
      })
      .describe('List of tags to assign.'),
    unassignTags: z
      .object({
        privateTags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that require an additional permission in order to access them, typically restricted from site members and visitors.'
          )
          .optional(),
        publicTags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that are exposed to anyone with access to the entity, including site members and visitors.'
          )
          .optional(),
      })
      .describe('List of tags to unassign.')
      .optional(),
  }),
});
export const BulkUpdateFunctionTagsResponse = /*#__PURE__*/ z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Provided only whenever possible. For example, `itemId` can't be provided when item creation has failed."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .min(0)
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action for this item was successful. When `false`, the `error` field is returned.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Item metadata.')
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .min(0)
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .min(0)
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .min(0)
        .optional(),
    })
    .describe('Bulk action metadata.')
    .optional(),
});
export const BulkUpdateFunctionTagsByFilterRequest = /*#__PURE__*/ z.object({
  filter: z.record(z.string(), z.any()).describe('Filter.'),
  options: z.object({
    assignTags: z
      .object({
        privateTags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that require an additional permission in order to access them, typically restricted from site members and visitors.'
          )
          .optional(),
        publicTags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that are exposed to anyone with access to the entity, including site members and visitors.'
          )
          .optional(),
      })
      .describe('List of tags to assign.'),
    unassignTags: z
      .object({
        privateTags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that require an additional permission in order to access them, typically restricted from site members and visitors.'
          )
          .optional(),
        publicTags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that are exposed to anyone with access to the entity, including site members and visitors.'
          )
          .optional(),
      })
      .describe('List of tags to unassign.')
      .optional(),
  }),
});
export const BulkUpdateFunctionTagsByFilterResponse = /*#__PURE__*/ z.object({
  jobId: z
    .string()
    .describe('Job ID for the bulk update operation.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
});
